import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';

import ReportFilters from './ReportFilters';
import ReportToolbar from './ReportToolbar';


const useStyles = makeStyles(theme => ({
    paper: {
        flexGrow: 1,
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(3),
    },
    reportFilters: {
        '& .MuiGrid-container .MuiGrid-item': { order: 1 },
        '& .MuiGrid-container .MuiGrid-item.report-filters--dropdown': { order: 3 },
        '& .MuiGrid-container .MuiGrid-item.report-filters--search': { order: 4 },
        '& .MuiGrid-container .MuiGrid-item.report-filters--multi-select-status': { order: 5 },
        '& .MuiGrid-container .MuiGrid-item.report-filters--submit': { order: 6 },
    },
    table: {
        minWidth: 650,
        '& .iconWrapper': {
            opacity: 0.9,
            verticalAlign: 'middle',
            cursor: 'pointer',
            paddingLeft: 2,
            paddingRight: 2,
            display: 'inline-block',
        }
    },
    coupleCol: {
        minWidth: 150,
    },
    officiantCol: {
        minWidth: 150,
    },
    tableHeader: {
        backgroundColor: theme.palette.primary.main,
        '& .MuiTableCell-head': {
            color: theme.palette.primary.contrastText,
        },
    },
    weddingDate: {
        minWidth: 165,
    },
    columnArrow: {
        '& .iconWrapper': {
            visibility: 'hidden',
        },
        '&:hover .iconWrapper': {
            opacity: 0.4,
            visibility: 'visible',
        },
    },
}));

const GenericWeddingsReport = () => {
    const classes = useStyles();
    const reportUrl = '/api/reports/weddings';
    const reportTitle = 'Referral Vendors';
    const pageTitle = `${reportTitle} - Enduring Promises`;
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(true);

    const now = new Date();
    const past = new Date(now.getFullYear(), 0, 1);
    const current = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours() + 1, 0, 0);

    const formatDate = val => {
        const month = val.getMonth() + 1;
        const day = val.getDate();
        return `${val.getFullYear()}-${month < 10 ? '0'+month : month}-${day < 10 ? '0'+day : day}`;
    }

    const genericDropdown = {
        id: 'vendor-field',
        label: 'Referral Vendor',
        state_key: 'vendor_field',
        multiple: true,
        options: [
            {value: 'Bridal Solutions', label: 'Bridal Solutions'},
            {value: 'Digital X,Digital X Entertainment and Quarum', label: 'Digital X'},
            {value: 'Dream Adventures', label: 'Dream Adventures'},
            {value: 'ED & Lottie Events', label: 'ED & Lottie Events'},
            {value: 'En Rose,En Rose Events,EnRose', label: 'En Rose'},
            {value: 'Kari Lynwood Events,Kari Lywood', label: 'Kari Lynwood Events'},
            {value: 'Lasting Moments', label: 'Lasting Moments'},
            {value: 'Megan Sawchuk', label: 'Megan Sawchuk'},
            {value: 'S2 Event Décor', label: 'S2 Event Décor'},
            {value: 'Other', label: 'Other'},
        ]
    }

    const [state, setState] = React.useState({
        start_date: formatDate(past),
        end_date: formatDate(current),
        multi_select_status: ['Complete'],
        vendor_field: [],
        input_with_tags: [],
        search: undefined,
    });

    const setStateHook = (state) => {
        const newState = {...state}
        if (!newState.vendor_field.includes('Other')) {
            newState.search = undefined;
        } else {
            newState.search = state.search || '';
        }
        setState(newState);
    }

    const [ordering, setOrderingState] = React.useState('wedding_date');
    const [direction, setDirection] = React.useState('asc');

    const setOrdering = value => {
        const newDirection = direction == 'asc' ? 'desc' : 'asc';
        if (value == ordering) {
            setDirection(newDirection);
        }
        setOrderingState(value);
    }

    const getData = state => {
        setLoading(true);
        const statuses = state.multi_select_status.join(',');
        const vendors = encodeURIComponent(state.vendor_field.join(','));
        const searchText = state.search !== undefined ? state.search : '';
        new Promise((resolve, reject) => {
            fetch(`${reportUrl}?start=${state.start_date}&end=${state.end_date}&orderBy=${ordering}&orderDirection=${direction}&status=${statuses}&vendorReferral=${vendors}&search=${searchText}`)
                .then(response => {
                    return response.json();
                })
                .then(result => {
                    setData(result);
                    resolve();
                })
                .finally(() => {
                    setLoading(false);
                });
        });
    };

    useEffect(() => {
        document.title = pageTitle;
        getData(state);
    }, [ordering, direction]);

    const handleSubmit = () => {
        getData(state);
    };

    const csvData = (data) => {
        const rows = [];
        Object.values(data).forEach(row => {
            rows.push([
                row.bride_full_name,
                row.groom_full_name,
                row.wedding_date_formatted,
                row.officiant ? row.officiant.name : '',
                row.venue,
                row.referral_vendor_venue
            ]);
        });
        return {
            filename: 'weddings-shows.csv',
            columns: [
                'Bride Name', 'Groom Name', 'Wedding Date', 'Officiant', 'Venue', 'Vendor Referral'
            ],
            rows,
        }
    }

    const getCellClassName = (key, extraClass) => {
        if (extraClass) {
            return ordering == key ? [classes.columnOrdered, extraClass].join(' ') : [classes.columnArrow, extraClass].join(' ');
        } else {
            return ordering == key ? classes.columnOrdered : classes.columnArrow;
        }
    };

    const sortDirectionIcon = direction === 'asc' ? 'arrow_upward' : 'arrow_downward';

    return (
        <Paper square className={classes.paper}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="h6">{reportTitle}</Typography>
                </Grid>
                <Grid item xs={9} className={classes.reportFilters}>
                    <ReportFilters
                        state={state}
                        setState={setStateHook}
                        handleSubmit={handleSubmit}
                        genericDropdown={genericDropdown}
                    />
                </Grid>
                <Grid item xs={3}>
                    <Grid
                        container
                        direction="row"
                        justify="flex-end"
                        alignItems="center"
                    >
                        {data !== null && <ReportToolbar data={csvData(data)} />}
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <TableContainer component={Paper} variant="outlined" square>
                        <Table className={classes.table} size="small">
                            <TableHead className={classes.tableHeader}>
                                <TableRow>
                                <TableCell align="left">Couple</TableCell>
                                    <TableCell
                                        align="left"
                                        className={getCellClassName('wedding_date', classes.weddingDate)}
                                    >
                                        Wedding Date
                                        <span className='iconWrapper'>
                                            <Icon fontSize='small' onClick={() => setOrdering('wedding_date')}>{sortDirectionIcon}</Icon>
                                        </span>
                                    </TableCell>
                                    <TableCell
                                        align="left"
                                        className={getCellClassName('officiant', classes.officiantCol)}
                                    >
                                        Officiant
                                        <span className='iconWrapper'>
                                            <Icon fontSize='small' onClick={() => setOrdering('officiant')}>{sortDirectionIcon}</Icon>
                                        </span>
                                    </TableCell>
                                    <TableCell
                                        align="left"
                                        className={getCellClassName('venue')}
                                    >
                                        Venue
                                        <span className='iconWrapper'>
                                            <Icon fontSize='small' onClick={() => setOrdering('venue')}>{sortDirectionIcon}</Icon>
                                        </span>
                                    </TableCell>
                                    <TableCell
                                        align="left"
                                        className={getCellClassName('vendorReferral')}
                                    >
                                        Vendor
                                        <span className='iconWrapper'>
                                            <Icon fontSize='small' onClick={() => setOrdering('vendorReferral')}>{sortDirectionIcon}</Icon>
                                        </span>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                            {!loading && data !== null && data.map((row, i) => {
                                const wedding = row;
                                return (
                                    <TableRow key={`row-${i}`}>
                                        <TableCell align="left" className={classes.coupleCol}>
                                            <IconButton component="a" href={`/admin/couples/${wedding.id}`}>
                                                <Icon>person</Icon>
                                            </IconButton>
                                            {wedding.bride_full_name} and {wedding.groom_full_name}
                                        </TableCell>
                                        <TableCell align="left">{wedding.wedding_date_formatted}</TableCell>
                                        <TableCell align="left">{wedding.officiant ? wedding.officiant.name : ''}</TableCell>
                                        <TableCell align="left">{wedding.venue}</TableCell>
                                        <TableCell align="left">{wedding.referral_vendor_venue}</TableCell>
                                    </TableRow>
                                )
                            })}
                            {!loading && data !== null && data.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">No data</TableCell>
                                </TableRow>
                            )}
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <CircularProgress color="primary" />
                                    </TableCell>
                                </TableRow>
                            )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </Paper>
    );
}

export default GenericWeddingsReport;
